import { useState, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { fromContentData } from '../../domain/routine/MissionContent';
import { TaskStatus } from '../../domain/routine/enums';
import type { DailyStep } from '../../domain/routine/models';

import { ChevronIcon, GripVerticalIcon } from '../icons';
import { CATEGORY_ICONS } from '../icons/maps';
import { Button } from './Button';
import { MissionCardMenu } from './MissionCardMenu';
import { StatusBadge } from './StatusBadge';
import { useTranslation } from '../design-system/data/translations';
import { resolveMissionTitle, resolveMissionObjective, resolveMissionInstruction } from '../design-system/data/missionI18n';

interface MissionAction {
  readonly key: 'start' | 'complete' | 'reset' | 'skip' | 'postpone';
  readonly labelKey: string;
  readonly onSelect: () => void;
  readonly danger?: boolean;
}

interface MissionCardProps {
  readonly step: DailyStep;
  readonly busy: boolean;
  readonly readOnly?: boolean;
  readonly activeForMode?: boolean;
  readonly onStart?: () => void;
  readonly onComplete?: () => void;
  readonly onReset?: () => void;
  readonly onSkip?: () => void;
  readonly onPostpone?: () => void;
  readonly reordering?: boolean;
  readonly position?: number;
  readonly total?: number;
  readonly onMoveUp?: () => void;
  readonly onMoveDown?: () => void;
  readonly dragHandleProps?: ButtonHTMLAttributes<HTMLButtonElement>;
}

export function MissionCard({
  step,
  busy,
  readOnly = false,
  activeForMode = true,
  onStart,
  onComplete,
  onReset,
  onSkip,
  onPostpone,
  reordering = false,
  position,
  total,
  onMoveUp,
  onMoveDown,
  dragHandleProps,
}: MissionCardProps) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();
  const translatedTitle = resolveMissionTitle(step.title, t);
  const CategoryIcon = CATEGORY_ICONS[step.category];
  const content = fromContentData(step.data);
  const detailId = `mission-detail-${step.id}`;

  const secondaryActions: readonly MissionAction[] = readOnly
    ? []
    : buildSecondaryActions(step.status, { onComplete, onReset, onSkip, onPostpone });
  const primary = readOnly
    ? undefined
    : buildPrimaryAction(step.status, { onStart, onComplete, onReset });

  return (
    <article
      className={`mission-card mission-card--${step.status}${activeForMode ? '' : ' mission-card--inactive-mode'}`}
      data-category={step.category}
      aria-labelledby={`mission-${step.id}`}
    >
      <HeaderContent
        as={reordering ? 'div' : 'button'}
        expanded={expanded}
        detailId={detailId}
        onToggle={() => setExpanded((current) => !current)}
        showChevron={!reordering}
      >
        <span className="mission-card__order" aria-label={t('mission.order_aria').replace('{n}', String(step.order + 1))}>
          {String(step.order + 1).padStart(2, '0')}
        </span>
        <CategoryIcon className="mission-card__category-icon" aria-hidden="true" />
        <span className="mission-card__category">{t(`category.${step.category}`)}</span>
        <span
          id={`mission-${step.id}`}
          className="mission-card__title"
          role="heading"
          aria-level={3}
        >
          {translatedTitle}
        </span>
        <span className="mission-card__meta">
          {step.suggestedTime ? <span>{step.suggestedTime}</span> : null}
          <span>{step.estimatedDurationMinutes} min</span>
          <span>{step.required ? t('priority.essentiel') : t('priority.recommande')}</span>
          {!activeForMode ? <span className="mission-card__mode-tag">{t('mission.out_of_mode')}</span> : null}
        </span>
        <StatusBadge status={step.status} />
      </HeaderContent>

      {reordering && position !== undefined && total !== undefined ? (
        <div className="mission-card__reorder-controls">
          <button
            type="button"
            className="drag-handle"
            aria-label={t('reorder.drag_mission').replace('{title}', translatedTitle)}
            aria-describedby={`position-${step.id}`}
            {...dragHandleProps}
          >
            <GripVerticalIcon className="drag-handle__icon" />
          </button>
          <span id={`position-${step.id}`} className="mission-card__position">
            {t('reorder.position')} {position} {t('reorder.position_sur')} {total}
          </span>
          <Button
            variant="quiet"
            onClick={onMoveUp}
            disabled={position === 1}
            aria-label={t('reorder.monter_mission').replace('{title}', translatedTitle)}
          >
            {t('action.monter')}
          </Button>
          <Button
            variant="quiet"
            onClick={onMoveDown}
            disabled={position === total}
            aria-label={t('reorder.descendre_mission').replace('{title}', translatedTitle)}
          >
            {t('action.descendre')}
          </Button>
        </div>
      ) : null}

      {!readOnly && !reordering ? (
        <div className="mission-card__footer">
          {step.postponedToDate ? (
            <span className="mission-card__postponed">{t('status.postponed_label')} {step.postponedToDate}</span>
          ) : primary ? (
            <Button
              variant={primary.key === 'complete' ? 'primary' : 'secondary'}
              onClick={primary.onSelect}
              disabled={busy}
              aria-label={t(primary.labelKey).replace('{title}', translatedTitle)}
              className="mission-card__primary"
            >
              {t(primary.labelKey)}
            </Button>
          ) : null}
          <MissionCardMenu
            label={t('mission.actions_aria').replace('{title}', translatedTitle)}
            disabled={busy}
            actions={secondaryActions.map((action) => ({
              label: t(action.labelKey),
              onSelect: action.onSelect,
              ...(action.danger ? { danger: action.danger } : {}),
            }))}
          />
        </div>
      ) : null}

      {expanded && !reordering ? (
        <div id={detailId} className="mission-card__detail">
          {content ? (
            <>
              <p className="mission-card__detail-heading">{t('mission.objective_label')}</p>
              <p className="mission-card__detail-text">{resolveMissionObjective(step.title, t) || content.objective}</p>
              <p className="mission-card__detail-heading">{t('mission.instructions_label')}</p>
              <ul className="mission-card__detail-list">
                {content.instructions.map((instruction, i) => (
                  <li key={instruction}>{resolveMissionInstruction(step.title, i, t) || instruction}</li>
                ))}
              </ul>
              {content.optionalTip ? (
                <p className="mission-card__detail-tip">{content.optionalTip}</p>
              ) : null}
              {content.provisional ? (
                <p className="mission-card__detail-provisional">
                  {t('mission.provisional')}{content.provisionalNote ? ` — ${content.provisionalNote}` : ''}
                </p>
              ) : null}
            </>
          ) : null}
          {step.description ? <p className="mission-card__note">{step.description}</p> : null}
          {step.note ? <p className="mission-card__note">{step.note}</p> : null}
          {!activeForMode ? (
            <p className="mission-card__mode-note">
              {t('mission.mode_note')}
            </p>
          ) : null}
          {!readOnly && secondaryActions.length > 0 ? (
            <div className="mission-card__detail-actions">
              {secondaryActions.map((action) => (
                <Button
                  key={action.key}
                  variant={action.danger ? 'danger' : 'quiet'}
                  onClick={action.onSelect}
                  disabled={busy}
                  aria-label={t(action.labelKey).replace('{title}', translatedTitle)}
                >
                  {t(action.labelKey)}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function buildPrimaryAction(
  status: DailyStep['status'],
  handlers: {
    onStart?: (() => void) | undefined;
    onComplete?: (() => void) | undefined;
    onReset?: (() => void) | undefined;
  },
): MissionAction | undefined {
  switch (status) {
    case TaskStatus.pending:
      return handlers.onStart
        ? { key: 'start', labelKey: 'action.start', onSelect: handlers.onStart }
        : undefined;
    case TaskStatus.inProgress:
      return handlers.onComplete
        ? { key: 'complete', labelKey: 'action.complete', onSelect: handlers.onComplete }
        : undefined;
    case TaskStatus.completed:
      return handlers.onReset
        ? { key: 'reset', labelKey: 'action.reset', onSelect: handlers.onReset }
        : undefined;
    case TaskStatus.skipped:
      return handlers.onStart
        ? { key: 'start', labelKey: 'action.reactivate', onSelect: handlers.onStart }
        : undefined;
    case TaskStatus.postponed:
      return undefined;
  }
}

function buildSecondaryActions(
  status: DailyStep['status'],
  handlers: {
    onComplete?: (() => void) | undefined;
    onReset?: (() => void) | undefined;
    onSkip?: (() => void) | undefined;
    onPostpone?: (() => void) | undefined;
  },
): readonly MissionAction[] {
  const actions: MissionAction[] = [];
  if (status === TaskStatus.pending && handlers.onComplete) {
    actions.push({ key: 'complete', labelKey: 'action.complete_direct', onSelect: handlers.onComplete });
  }
  if ((status === TaskStatus.inProgress || status === TaskStatus.skipped) && handlers.onReset) {
    actions.push({ key: 'reset', labelKey: 'action.reset', onSelect: handlers.onReset });
  }
  if (status === TaskStatus.pending || status === TaskStatus.inProgress) {
    if (handlers.onSkip) {
      actions.push({ key: 'skip', labelKey: 'action.skip', onSelect: handlers.onSkip, danger: true });
    }
  }
  if (
    (status === TaskStatus.pending ||
      status === TaskStatus.inProgress ||
      status === TaskStatus.skipped) &&
    handlers.onPostpone
  ) {
    actions.push({ key: 'postpone', labelKey: 'action.postpone', onSelect: handlers.onPostpone });
  }
  return actions;
}

interface HeaderContentProps {
  readonly as: 'button' | 'div';
  readonly expanded: boolean;
  readonly detailId: string;
  readonly onToggle: () => void;
  readonly showChevron: boolean;
  readonly children: ReactNode;
}

/** Renders a real toggle button in normal use; a plain, non-interactive header during
 * reordering so the accordion never competes with the drag handle for taps. */
function HeaderContent({
  as,
  expanded,
  detailId,
  onToggle,
  showChevron,
  children,
}: HeaderContentProps) {
  if (as === 'div') {
    return <div className="mission-card__toggle mission-card__toggle--static">{children}</div>;
  }
  return (
    <button
      type="button"
      className="mission-card__toggle"
      aria-expanded={expanded}
      aria-controls={detailId}
      onClick={onToggle}
    >
      {children}
      {showChevron ? (
        <ChevronIcon
          width={18}
          height={18}
          className={`mission-card__chevron${expanded ? ' is-open' : ''}`}
        />
      ) : null}
    </button>
  );
}
